import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import SingleSelect from "@/components/controls/SingleSelect";
import { Button } from "@/components/ui/button";
import Input from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import Text from "@/components/ui/text";
import { Textarea } from "@/components/ui/textarea";
import { useUsers } from "@/entities/users/hooks";
import { useUserSelect } from "@/entities/users/useUserSelect";
import { useSaveSubmissionFulfillment } from "@/entities/stripeCheckout/hooks";
import { FulfillmentTask } from "@/entities/stripeCheckout/types";
import { webFormErrorDetail } from "@/entities/webForm/hooks";
import {
  FulfillmentDraft,
  MAX_DUE_DAYS,
  MAX_TITLE_TEMPLATE_LENGTH,
  STRIPE_CHECKOUT_PREFIX,
  fulfillmentDraft,
  fulfillmentErrors,
  fulfillmentUpdate
} from "@/pages/settings/components/integrations/stripeCheckout/stripeCheckoutOptions";
import Section from "@/pages/settings/components/sections/Section";

const PREFIX = `${STRIPE_CHECKOUT_PREFIX}.fulfillment`;

const SELECT_CLASSNAME = "text-start border border-border-border2 pl-0";
const SELECT_TRIGGER_CLASSNAME = "w-full min-h-8 h-9";
const FIELD_CLASSNAME =
  "w-[240px] rounded-lg bg-surface-field-default shadow-field outline-none";

interface FulfillmentSectionProps {
  task: FulfillmentTask | null | undefined;
  isLoading: boolean;
  subTitle: string;
  isBusy: boolean;
}

// The task created for each new contract, so someone starts onboarding.
export const FulfillmentSection: React.FC<FulfillmentSectionProps> = ({
  task,
  isLoading,
  subTitle,
  isBusy
}) => {
  const { t } = useTranslation();
  const save = useSaveSubmissionFulfillment();
  const userSelect = useUserSelect();
  const [saveError, setSaveError] = useState<string | null>(null);
  const isLoaded = task !== undefined;

  const [draft, setDraft] = useState<FulfillmentDraft>(() =>
    fulfillmentDraft(task ?? null)
  );
  // Show what was saved once it loads or changes, replacing any older draft.
  const saved = isLoaded
    ? JSON.stringify(fulfillmentUpdate(fulfillmentDraft(task)))
    : null;
  const [synced, setSynced] = useState(saved);
  if (saved !== synced) {
    setSynced(saved);
    if (isLoaded) setDraft(fulfillmentDraft(task));
  }

  // The chosen assignee resolves by id, so it shows by name whatever the
  // search shows.
  const assigneeIds = useMemo(
    () => (draft.assigneeId ? [draft.assigneeId] : []),
    [draft.assigneeId]
  );
  const chosenAssignee = useUsers(assigneeIds, {
    enabled: assigneeIds.length > 0
  });
  const options = useMemo(() => {
    const byId = new Map<string, string>();
    chosenAssignee.users.forEach((user) => {
      if (user?.id) byId.set(user.id, user.displayName);
    });
    userSelect.options.forEach((option) =>
      byId.set(option.id, option.displayName)
    );
    return [...byId].map(([id, displayName]) => ({ id, displayName }));
  }, [chosenAssignee.users, userSelect.options]);

  const update = fulfillmentUpdate(draft);
  const errors = fulfillmentErrors(draft);
  const disabled = !isLoaded || isBusy || save.isPending;
  const isDirty = saved !== null && JSON.stringify(update) !== saved;
  const canSave = isDirty && errors.length === 0 && !disabled;
  const fieldsDisabled = disabled || !draft.enabled;

  const edit = (changes: Partial<FulfillmentDraft>) => {
    setDraft((current) => ({ ...current, ...changes }));
    setSaveError(null);
  };

  const handleSave = () => {
    setSaveError(null);
    save.mutate(
      { data: update },
      {
        onSuccess: () => toast.success(t(`${PREFIX}.saved`)),
        onError: (error) => {
          const message = webFormErrorDetail(error) ?? t(`${PREFIX}.saveError`);
          setSaveError(message);
          toast.error(message);
        }
      }
    );
  };

  return (
    <Section
      title={t(`${PREFIX}.title`)}
      subTitle={subTitle}
      isLoading={isLoading}
      items={[
        {
          title: t(`${PREFIX}.enabled.title`),
          subTitle: t(`${PREFIX}.enabled.subtitle`),
          customAction: (
            <Switch
              data-test="stripe-checkout-fulfillment-enabled"
              aria-label={t(`${PREFIX}.enabled.title`)}
              checked={draft.enabled}
              onCheckedChange={(enabled) => edit({ enabled })}
              disabled={disabled}
            />
          )
        },
        {
          title: t(`${PREFIX}.assignee.title`),
          subTitle: t(`${PREFIX}.assignee.subtitle`),
          customAction: (
            <div className="w-[240px]" data-test="stripe-checkout-assignee">
              <SingleSelect
                fieldName="stripeCheckoutAssignee"
                options={options}
                selectedValue={draft.assigneeId ?? undefined}
                onSelection={(value) => edit({ assigneeId: String(value) })}
                formatter={(value) =>
                  options.find((option) => option.id === value)?.displayName ??
                  String(value)
                }
                placeholder={t(`${PREFIX}.assignee.placeholder`)}
                showSearch
                isOpen={userSelect.isOpen}
                onOpenChange={userSelect.handleOpenChange}
                onSearch={userSelect.setSearchTerm}
                isLoading={userSelect.isLoading}
                renderIcon={userSelect.renderUserIcon}
                disabled={fieldsDisabled}
                className={SELECT_CLASSNAME}
                popoverTriggerClassName={SELECT_TRIGGER_CLASSNAME}
                variant="form"
              />
            </div>
          )
        },
        {
          title: t(`${PREFIX}.titleTemplate.title`),
          subTitle: t(`${PREFIX}.titleTemplate.subtitle`),
          customAction: (
            <Input
              data-test="stripe-checkout-task-title"
              aria-label={t(`${PREFIX}.titleTemplate.title`)}
              value={draft.titleTemplate}
              onChange={(event) => edit({ titleTemplate: event.target.value })}
              maxLength={MAX_TITLE_TEMPLATE_LENGTH}
              disabled={fieldsDisabled}
              containerClassName="w-[240px]"
            />
          )
        },
        {
          title: t(`${PREFIX}.checklist.title`),
          subTitle: t(`${PREFIX}.checklist.subtitle`),
          customAction: (
            <Textarea
              data-test="stripe-checkout-checklist"
              aria-label={t(`${PREFIX}.checklist.title`)}
              placeholder={t(`${PREFIX}.checklist.placeholder`)}
              value={draft.checklistText}
              onChange={(event) => edit({ checklistText: event.target.value })}
              rows={4}
              disabled={fieldsDisabled}
              className={FIELD_CLASSNAME}
            />
          )
        },
        {
          title: t(`${PREFIX}.dueDays.title`),
          subTitle: t(`${PREFIX}.dueDays.subtitle`, { max: MAX_DUE_DAYS }),
          customAction: (
            <Input
              data-test="stripe-checkout-due-days"
              aria-label={t(`${PREFIX}.dueDays.title`)}
              type="number"
              min={0}
              max={MAX_DUE_DAYS}
              value={draft.dueInBusinessDays}
              onChange={(event) =>
                edit({ dueInBusinessDays: event.target.value })
              }
              disabled={fieldsDisabled}
              containerClassName="w-[120px]"
            />
          )
        }
      ]}
    >
      {(errors.length > 0 || saveError) && (
        <div
          className="flex flex-col gap-1"
          role="alert"
          data-test="stripe-checkout-fulfillment-error"
        >
          {errors.map((error) => (
            <Text key={error} variant="smBodyError">
              {t(`${PREFIX}.errors.${error}`, { max: MAX_DUE_DAYS })}
            </Text>
          ))}
          {saveError && <Text variant="smBodyError">{saveError}</Text>}
        </div>
      )}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!canSave}
          data-test="stripe-checkout-save-fulfillment"
        >
          {t(`${PREFIX}.save`)}
        </Button>
      </div>
    </Section>
  );
};
